import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/root';
import { setSelectedForm } from '../../redux/slices/thresholdFormSlice';
import { useGetInspectionFormQuery } from '../../redux/rtkQuery/apiFormbuilderSlice';

/**
 * Fills in `selectedForm.fileBase64` — the form's page images — on demand.
 *
 * The forms list endpoint no longer ships page images: they were ~99% of a
 * multi-megabyte response that every inspection open had to wait for, just to
 * work out which form matched. `selectedForm` is therefore assigned from a list
 * row that has no images, and whatever actually draws the form asks for them
 * here.
 *
 * `fileBase64` distinguishes three states, and the difference matters:
 *   undefined -> not fetched yet            (fetch, and report loading)
 *   null      -> fetched, form has no pages (a real answer; do not re-fetch)
 *   []/[...]  -> fetched                    (done)
 *
 * Callers MUST gate on the returned `imagesLoading` before rendering or
 * generating a PDF. `overlayInfo` treats missing images as "draw nothing",
 * so a report produced mid-fetch is not an error — it is a silently blank
 * form with the values floating on it, and it gets uploaded and attached.
 */
const useSelectedFormImages = () => {
  const dispatch = useDispatch();
  const selectedForm = useSelector((s: RootState) => s.thresholdForms.selectedForm);

  const formId = selectedForm?.id;
  const hasImages = !!formId && (selectedForm as any)?.fileBase64 !== undefined;

  // Subscribed for as long as a form is selected, NOT skipped once hydrated.
  //
  // `selectedForm` is a detached copy in Redux, so invalidating the RTK cache
  // alone changed nothing on screen — replacing a form's document left the old
  // pages up until a full page refresh. Staying subscribed means the refetch
  // that invalidation triggers actually reaches us. It costs nothing while
  // nothing changes: only the document-replacing mutation invalidates
  // `thresholdFormFile`, so markup autosaves still never re-pull images.
  // `currentData`, NOT `data`. RTK Query's `data` deliberately "tries to hold
  // data as much as possible, also re-using data from the last arguments passed
  // into the hook" — so after viewing form 20 and opening form 19, `data` is
  // still form 20's payload until 19's response lands. That is long enough to
  // do real damage: the merge below accepted form 20's page images as form 19's,
  // which flipped `imagesLoading` false early, and the builder then loaded its
  // template from form 20's markup. The images were corrected a moment later
  // when 19 arrived; the fields were not, because the builder only seeds a
  // template while its state is empty. Result: form 19's pages wearing form 20's
  // 25 fields. `currentData` is only ever the response for the current formId.
  const { currentData, isError, isFetching } = useGetInspectionFormQuery(formId, { skip: !formId });

  // Compare what the form CURRENTLY holds against what the server sent.
  //
  // Deliberately stateless. A ref remembering "I already merged X" looked
  // equivalent and was not: clicking Configure Form dispatches a fresh list row
  // with no images at all, and the ref would still claim that id was merged, so
  // the row was never re-populated and the builder kept showing the previous
  // document until a full reload. State, not history, decides.
  //
  // O(1): page count plus the LENGTH of the first page's base64, never its bytes.
  // `undefined` (never fetched) must not collapse into `null`/`[]` (fetched, no
  // pages), so it gets its own marker.
  const imageSignature = (images: any) =>
    images === undefined
      ? 'absent'
      : `${(images || []).length}|${(images || [])[0]?.base64?.length ?? 0}`;

  // `!isFetching` matters: RTK serves the previous response while revalidating,
  // so merging mid-flight would paint the OLD document for a beat before the new
  // one lands — the very thing this is meant to stop. Waiting keeps
  // `imagesLoading` true instead, and consumers show their loading state.
  const serverImages = currentData ? currentData.fileBase64 : undefined;
  const needsMerge =
    !!currentData &&
    !isFetching &&
    imageSignature((selectedForm as any)?.fileBase64) !== imageSignature(serverImages);

  useEffect(() => {
    if (!needsMerge) {
      return;
    }
    // Merge rather than replace: the list row is the authority on everything
    // else, and `formMarkup` in particular may already carry edits made since
    // it was assigned. Only the document fields come from the detail read.
    dispatch(
      setSelectedForm({
        ...selectedForm,
        fileBase64: currentData.fileBase64 ?? null,
        sourceFile: currentData.sourceFile ?? null,
        visibleFilename: currentData.visibleFilename ?? (selectedForm as any)?.visibleFilename,
      } as any),
    );
  }, [needsMerge, currentData, formId]);

  return {
    // Only the FIRST load blocks. A background refresh after a document swap
    // must not disable Create Report on a form that already has usable pages.
    imagesLoading: !!formId && !hasImages && !isError,
    imagesFailed: isError,

    // The server's own `form_markup` for this form, and whether that read has
    // actually landed.
    //
    // `selectedForm.formMarkup` is a Redux copy taken from a list row, and a
    // caller cannot tell "this form has no fields" from "this copy does not
    // have them yet". The builder has to make exactly that call before seeding
    // a blank template over a form, so it needs the authoritative answer rather
    // than the convenient one. Same response the pages come from, so the two
    // can never disagree.
    savedMarkup: currentData ? currentData.formMarkup : undefined,
    markupLoaded: !!currentData && !isFetching,
  };
};

export default useSelectedFormImages;
